import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    IconButton,
    Grid,
    Card,
    Tabs,
    Tab,
    TextField,
    MenuItem,
    InputAdornment,
    TablePagination
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import { makeApiRequest } from '../../../utils/api';

const MODULE_TABS = [
    { value: 'all', label: 'All' },
    { value: 'certificate', label: 'Certificate' },
    { value: 'assessment', label: 'Assessment / Marks', modules: ['marks', 'batch_assessments'] },
    { value: 'student_profile', label: 'Student Profile' },
    { value: 'batch_profile', label: 'Batch Profile' },
    { value: 'course', label: 'Course Edit Requests' },
];

export default function EditHistory() {
    const token = localStorage.getItem('authToken');
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState([]);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // Details Modal State
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [moduleNameMap, setModuleNameMap] = useState({});

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        fetchHistory(0, rowsPerPage, activeTab, statusFilter, searchTerm);
        fetchModuleNames();
    }, []);

    const fetchModuleNames = async () => {
        try {
            const response = await makeApiRequest('post', '/api/module/list', { token });
            const modules = response?.status === 'success' ? (response.data?.data || []) : [];
            const map = {};
            modules.forEach(m => { map[m.module_id] = m.module; });
            setModuleNameMap(map);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchHistory = async (
        currentPage = page,
        currentRowsPerPage = rowsPerPage,
        module = activeTab,
        status = statusFilter,
        search = searchTerm
    ) => {
        setLoading(true);
        setError(null);
        try {
            const response = await makeApiRequest('post', '/api/edit-requests/history', {
                token,
                page: currentPage + 1,
                per_page: currentRowsPerPage,
                module,
                status,
                search,
            });
            if (response && response.status === 'success') {
                if (response.data && Array.isArray(response.data.data)) {
                    setHistory(response.data.data);
                    setTotalCount(response.data.total || 0);
                } else if (Array.isArray(response.data)) {
                    setHistory(response.data);
                    setTotalCount(response.data.length);
                }
            } else {
                setError('Failed to fetch history.');
            }
        } catch (err) {
            console.error(err);
            setError('Error loading edit requests history.');
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (e, newTab) => {
        setActiveTab(newTab);
        setPage(0);
        fetchHistory(0, rowsPerPage, newTab, statusFilter, searchTerm);
    };

    const handleApplyFilters = () => {
        setPage(0);
        fetchHistory(0, rowsPerPage, activeTab, statusFilter, searchTerm);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        fetchHistory(newPage, rowsPerPage, activeTab, statusFilter, searchTerm);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        fetchHistory(0, newRowsPerPage, activeTab, statusFilter, searchTerm);
    };

    const handleOpenDetails = (request) => {
        setSelectedRequest(request);
        setOpenModal(true);
    };

    const handleCloseDetails = () => {
        setOpenModal(false);
        setSelectedRequest(null);
    };

    const renderMarksComparison = (request) => {
        if (!request || request.module !== 'marks') return null;

        const oldMap = {};
        request.old_data.forEach(item => {
            oldMap[item.assessment_name] = item.marks;
        });

        return (
            <Table size="small" sx={{ mt: 1 }}>
                <TableHead sx={{ backgroundColor: '#F1F4F9' }}>
                    <TableRow>
                        <TableCell>Assessment Name</TableCell>
                        <TableCell>Field Name</TableCell>
                        <TableCell align="center">Old Mark</TableCell>
                        <TableCell align="center">Proposed Mark</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {request.new_data.map(change => {
                        const oldMarks = oldMap[change.assessment_name] || {};
                        const fields = Object.keys(change.marks);
                        return fields.map((field, idx) => (
                            <TableRow key={`${change.assessment_name}-${field}`}>
                                {idx === 0 && (
                                    <TableCell rowSpan={fields.length} sx={{ fontWeight: 600, verticalAlign: 'top' }}>
                                        {change.assessment_name}
                                    </TableCell>
                                )}
                                <TableCell>{field.replace(/_/g, ' ').toUpperCase()}</TableCell>
                                <TableCell align="center" sx={{ color: 'error.main', fontWeight: 500 }}>
                                    {oldMarks[field] !== undefined && oldMarks[field] !== null ? oldMarks[field] : 'N/A'}
                                </TableCell>
                                <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>
                                    {change.marks[field] !== undefined && change.marks[field] !== null ? change.marks[field] : 'N/A'}
                                </TableCell>
                            </TableRow>
                        ));
                    })}
                </TableBody>
            </Table>
        );
    };

    const CERTIFICATE_FIELD_LABELS = {
        date: 'Issue Date',
        student_name: 'Student Name',
        course: 'Course Name',
        start_date: 'Start Date',
        end_date: 'End Date',
    };

    const renderCertificateComparison = (request) => {
        if (!request || request.module !== 'certificate') return null;

        const oldData = request.old_data || {};
        const newData = request.new_data || {};
        const changedFields = Object.keys(newData).filter(key => key !== 'marks' && key !== 'image');

        return (
            <>
                {newData.image && (
                    <>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Profile Photo</Typography>
                        <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" color="textSecondary">Previous</Typography>
                                <Box
                                    component="img"
                                    src={oldData.image ? `${process.env.REACT_APP_URL}/documents/${oldData.image}` : ''}
                                    alt="Previous profile"
                                    sx={{ display: 'block', width: 100, height: 100, objectFit: 'cover', borderRadius: 1, border: '2px solid', borderColor: 'error.main' }}
                                />
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" color="textSecondary">Applied</Typography>
                                <Box
                                    component="img"
                                    src={`${process.env.REACT_APP_URL}/documents/${newData.image}`}
                                    alt="Applied profile"
                                    sx={{ display: 'block', width: 100, height: 100, objectFit: 'cover', borderRadius: 1, border: '2px solid', borderColor: 'success.main' }}
                                />
                            </Box>
                        </Box>
                    </>
                )}
                {changedFields.length > 0 && (
                    <Table size="small" sx={{ mt: 1 }}>
                        <TableHead sx={{ backgroundColor: '#F1F4F9' }}>
                            <TableRow>
                                <TableCell>Field</TableCell>
                                <TableCell align="center">Old Value</TableCell>
                                <TableCell align="center">New Value</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {changedFields.map(field => (
                                <TableRow key={field}>
                                    <TableCell sx={{ fontWeight: 600 }}>{CERTIFICATE_FIELD_LABELS[field] || field}</TableCell>
                                    <TableCell align="center" sx={{ color: 'error.main', fontWeight: 500 }}>
                                        {oldData[field] || 'N/A'}
                                    </TableCell>
                                    <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>
                                        {newData[field] || 'N/A'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                {Array.isArray(newData.marks) && newData.marks.length > 0 && (
                    <>
                        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>Marks Comparison</Typography>
                        <Table size="small" sx={{ mt: 1 }}>
                            <TableHead sx={{ backgroundColor: '#F1F4F9' }}>
                                <TableRow>
                                    <TableCell>Assessment Name</TableCell>
                                    <TableCell>Field Name</TableCell>
                                    <TableCell align="center">Old Mark</TableCell>
                                    <TableCell align="center">Proposed Mark</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {newData.marks.map(change => {
                                    const oldEntry = (oldData.marks || []).find(o => o.assessment_id === change.assessment_id) || {};
                                    const oldMarks = oldEntry.marks || {};
                                    const fields = Object.keys(change.marks);
                                    return fields.map((field, idx) => (
                                        <TableRow key={`${change.assessment_name}-${field}`}>
                                            {idx === 0 && (
                                                <TableCell rowSpan={fields.length} sx={{ fontWeight: 600, verticalAlign: 'top' }}>
                                                    {change.assessment_name}
                                                </TableCell>
                                            )}
                                            <TableCell>{field.replace(/_/g, ' ').toUpperCase()}</TableCell>
                                            <TableCell align="center" sx={{ color: 'error.main', fontWeight: 500 }}>
                                                {oldMarks[field] !== undefined && oldMarks[field] !== null ? oldMarks[field] : 'N/A'}
                                            </TableCell>
                                            <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>
                                                {change.marks[field] !== undefined && change.marks[field] !== null ? change.marks[field] : 'N/A'}
                                            </TableCell>
                                        </TableRow>
                                    ));
                                })}
                            </TableBody>
                        </Table>
                    </>
                )}
            </>
        );
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'approved':
                return <Chip label="Approved" size="small" color="success" />;
            case 'rejected':
                return <Chip label="Rejected" size="small" color="error" />;
            default:
                return <Chip label="Pending" size="small" color="warning" />;
        }
    };

    const getRequestDisplayInfo = (req) => {
        const isBatch = req.module === 'batch_assessments' || req.module === 'batch_profile';
        const isCertificate = req.module === 'certificate';
        if (req.module === 'course') {
            return { name: req.course?.course_name || req.old_data?.course_name || 'N/A', id: `Course ID: ${req.reference_id ?? 'N/A'}` };
        }
        if (isBatch) {
            return { name: req.batch?.batch_name || 'N/A', id: `Batch ID: ${req.batch?.id ?? 'N/A'}` };
        }
        if (isCertificate) {
            return { name: req.certificate?.student_name || 'N/A', id: req.certificate?.user_id || 'N/A' };
        }
        return { name: req.student?.name || 'N/A', id: req.student?.userid || 'N/A' };
    };

    const filteredHistory = history.filter(req => {
        const tabDef = MODULE_TABS.find(t => t.value === activeTab);
        if (tabDef && tabDef.value !== 'all') {
            const moduleMatch = tabDef.modules ? tabDef.modules.includes(req.module) : req.module === tabDef.value;
            if (!moduleMatch) return false;
        }

        if (statusFilter !== 'all' && req.status !== statusFilter) return false;

        if (searchTerm.trim()) {
            const term = searchTerm.trim().toLowerCase();
            const { name, id } = getRequestDisplayInfo(req);
            const haystack = [name, id, req.requestor?.name].filter(Boolean).join(' ').toLowerCase();
            if (!haystack.includes(term)) return false;
        }

        return true;
    });
    const renderStudentProfileComparison = (request) => {
        if (!request || request.module !== 'student_profile') return null;

        const diffs = [];
        const findDiffs = (oldObj, newObj, prefix = '') => {
            if (!oldObj || !newObj) return;
            Object.keys(newObj).forEach(key => {
                const oldVal = oldObj[key];
                const newVal = newObj[key];
                
                if (typeof newVal === 'object' && newVal !== null) {
                    findDiffs(oldVal || {}, newVal, prefix ? `${prefix} > ${key}` : key);
                } else if (oldVal !== newVal) {
                    if (key.includes('file') || key.includes('copy') || key.includes('path') || key === 'token' || key === 'password') return;
                    diffs.push({
                        field: prefix ? `${prefix} > ${key}` : key,
                        oldVal: oldVal !== undefined && oldVal !== null ? String(oldVal) : 'N/A',
                        newVal: newVal !== undefined && newVal !== null ? String(newVal) : 'N/A'
                    });
                }
            });
        };

        findDiffs(request.old_data, request.new_data);

        return (
            <Table size="small" sx={{ mt: 1 }}>
                <TableHead sx={{ backgroundColor: '#F1F4F9' }}>
                    <TableRow>
                        <TableCell>Field Name</TableCell>
                        <TableCell align="center">Old Value</TableCell>
                        <TableCell align="center">Proposed Value</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {diffs.map(diff => (
                        <TableRow key={diff.field}>
                            <TableCell sx={{ fontWeight: 600 }}>{diff.field.replace(/_/g, ' ').toUpperCase()}</TableCell>
                            <TableCell align="center" sx={{ color: 'error.main', fontWeight: 500 }}>
                                {diff.oldVal}
                            </TableCell>
                            <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>
                                {diff.newVal}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    };

    const renderBatchProfileComparison = (request) => {
        if (!request || request.module !== 'batch_profile') return null;

        const diffs = [];
        const keys = ['batch_name', 'start_date', 'end_date', 'location', 'course', 'status'];
        
        keys.forEach(key => {
            const oldVal = request.old_data[key];
            const newVal = request.new_data[key];
            if (oldVal !== newVal) {
                diffs.push({
                    field: key,
                    oldVal: oldVal !== undefined && oldVal !== null ? String(oldVal) : 'N/A',
                    newVal: newVal !== undefined && newVal !== null ? String(newVal) : 'N/A'
                });
            }
        });

        return (
            <Table size="small" sx={{ mt: 1 }}>
                <TableHead sx={{ backgroundColor: '#F1F4F9' }}>
                    <TableRow>
                        <TableCell>Field Name</TableCell>
                        <TableCell align="center">Old Value</TableCell>
                        <TableCell align="center">Proposed Value</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {diffs.map(diff => (
                        <TableRow key={diff.field}>
                            <TableCell sx={{ fontWeight: 600 }}>{diff.field.replace(/_/g, ' ').toUpperCase()}</TableCell>
                            <TableCell align="center" sx={{ color: 'error.main', fontWeight: 500 }}>
                                {diff.field === 'location' ? (diff.oldVal === '1' ? 'Chennai' : 'Trichy') : diff.oldVal}
                            </TableCell>
                            <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>
                                {diff.field === 'location' ? (diff.newVal === '1' ? 'Chennai' : 'Trichy') : diff.newVal}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    };

    const renderCourseComparison = (request) => {
        if (!request || request.module !== 'course') return null;

        const oldData = request.old_data || {};
        const newData = request.new_data || {};

        // Parse module_ids into a numeric id array
        const toIdArray = (val) => {
            if (Array.isArray(val)) return val.map(Number);
            if (typeof val === 'string') return val.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n));
            return [];
        };

        const oldModuleIds = toIdArray(oldData.module_ids);
        const newModuleIds = toIdArray(newData.module_ids);
        const addedModuleIds = newModuleIds.filter(id => !oldModuleIds.includes(id));
        const removedModuleIds = oldModuleIds.filter(id => !newModuleIds.includes(id));
        const getModuleName = (id) => moduleNameMap[id] || `Module ${id}`;

        const scalarFields = [];

        if (oldData.course_name !== newData.course_name) {
            scalarFields.push({ key: 'course_name', label: 'Course Name', oldVal: oldData.course_name, newVal: newData.course_name });
        }

        if (addedModuleIds.length > 0 || removedModuleIds.length > 0) {
            scalarFields.push({
                key: 'module_ids',
                label: 'Modules Changed',
                oldVal: removedModuleIds.length > 0 ? removedModuleIds.map(getModuleName).join(', ') : '—',
                newVal: addedModuleIds.length > 0 ? addedModuleIds.map(getModuleName).join(', ') : '—',
            });
        }

        const oldAssessments = Array.isArray(oldData.assessments) ? oldData.assessments : [];
        const newAssessments = Array.isArray(newData.assessments) ? newData.assessments : [];
        const allGenericNames = Array.from(new Set([
            ...oldAssessments.map(a => a.generic_name),
            ...newAssessments.map(a => a.generic_name),
        ]));

        return (
            <>
                {scalarFields.length > 0 && (
                    <Table size="small" sx={{ mt: 1 }}>
                        <TableHead sx={{ backgroundColor: '#F1F4F9' }}>
                            <TableRow>
                                <TableCell>Field</TableCell>
                                <TableCell align="center">Old Value</TableCell>
                                <TableCell align="center">Proposed Value</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {scalarFields.map(f => (
                                <TableRow key={f.key} sx={{ backgroundColor: f.oldVal !== f.newVal ? '#fffde7' : 'inherit' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>{f.label}</TableCell>
                                    <TableCell align="center" sx={{ color: 'error.main', fontWeight: 500 }}>{f.oldVal ?? 'N/A'}</TableCell>
                                    <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>{f.newVal ?? 'N/A'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}

                {allGenericNames.length > 0 && (
                    <>
                        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>Assessments</Typography>
                        <Table size="small" sx={{ mt: 1 }}>
                            <TableHead sx={{ backgroundColor: '#F1F4F9' }}>
                                <TableRow>
                                    <TableCell>Assessment (Generic Name)</TableCell>
                                    <TableCell align="center">Old Display Name</TableCell>
                                    <TableCell align="center">Proposed Display Name</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allGenericNames.map(generic => {
                                    const oldItem = oldAssessments.find(a => a.generic_name === generic);
                                    const newItem = newAssessments.find(a => a.generic_name === generic);
                                    const oldName = oldItem?.specific_name ?? '—';
                                    const newName = newItem?.specific_name ?? '—';
                                    const changed = oldName !== newName;
                                    return (
                                        <TableRow key={generic} sx={{ backgroundColor: changed ? '#fffde7' : 'inherit' }}>
                                            <TableCell sx={{ fontWeight: 600 }}>{generic}</TableCell>
                                            <TableCell align="center" sx={{ color: 'error.main', fontWeight: 500 }}>{oldName}</TableCell>
                                            <TableCell align="center" sx={{ color: 'success.main', fontWeight: changed ? 600 : 400 }}>{newName}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </>
                )}
            </>
        );
    };

    return (
        <Box sx={{ p: 4, backgroundColor: '#F1F4F9', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ color: '#673ab7', fontWeight: 600 }}>
                    Edit Requests Audit Log
                </Typography>
            </Box>

            <Card sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ borderBottom: '1px solid #E0E0E0' }}
                >
                    {MODULE_TABS.map(tab => (
                        <Tab key={tab.value} value={tab.value} label={tab.label} />
                    ))}
                </Tabs>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, p: 2, alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Search by student, batch, or requestor"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
                        sx={{ minWidth: 300, flexGrow: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        size="small"
                        select
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        sx={{ minWidth: 160 }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                    </TextField>
                    <Button
                        variant="contained"
                        sx={{ backgroundColor: '#673ab7', '&:hover': { backgroundColor: '#512da8' } }}
                        onClick={handleApplyFilters}
                    >
                        Search / Filter
                    </Button>
                </Box>
            </Card>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                    <CircularProgress size={50} />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {!loading && history.length === 0 && (
                <Alert severity="info">
                    No historical edit requests found.
                </Alert>
            )}

            {!loading && history.length > 0 && activeTab === 'course' && (
                <Paper sx={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ backgroundColor: '#DCE3E7' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Course Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Requested By</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Submitted At</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Remarks</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Details</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {history.map(req => (
                                    <TableRow key={req.id} hover>
                                        <TableCell>{req.course?.course_name || req.old_data?.course_name || 'N/A'}</TableCell>
                                        <TableCell>{req.requestor?.name || 'N/A'}</TableCell>
                                        <TableCell>{new Date(req.created_at).toLocaleString()}</TableCell>
                                        <TableCell>{getStatusChip(req.status)}</TableCell>
                                        <TableCell>{req.remarks || '—'}</TableCell>
                                        <TableCell align="center">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<VisibilityIcon />}
                                                onClick={() => handleOpenDetails(req)}
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            )}

            {!loading && history.length > 0 && activeTab !== 'course' && (
                <Paper sx={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ backgroundColor: '#DCE3E7' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Requestor</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Student Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Student ID</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Batch</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Course</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Approver</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Updated At</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Details</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {history.map(req => {
                                    const isBatch = req.module === 'batch_assessments' || req.module === 'batch_profile';
                                    const isCertificate = req.module === 'certificate';
                                    return (
                                    <TableRow key={req.id} hover>
                                        <TableCell>{req.requestor?.name || 'N/A'}</TableCell>
                                        <TableCell>{isBatch ? (req.batch?.batch_name || 'N/A') : isCertificate ? (req.certificate?.student_name || 'N/A') : (req.student?.name || 'N/A')}</TableCell>
                                        <TableCell>{isBatch ? `Batch ID: ${req.batch?.id}` : isCertificate ? (req.certificate?.user_id || 'N/A') : (req.student?.userid || 'N/A')}</TableCell>
                                        <TableCell>{isBatch ? (req.batch?.batch_name || 'N/A') : isCertificate ? (req.certificate?.batch || 'N/A') : (req.student?.batch?.batch_name || 'N/A')}</TableCell>
                                        <TableCell>{isBatch ? (req.batch?.course?.course_name || 'N/A') : isCertificate ? (req.certificate?.course || 'N/A') : (req.student?.course?.course_name || 'N/A')}</TableCell>
                                        <TableCell>{getStatusChip(req.status)}</TableCell>
                                        <TableCell>{req.approver?.name || '-'}</TableCell>
                                        <TableCell>{new Date(req.updated_at).toLocaleString()}</TableCell>
                                        <TableCell align="center">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<VisibilityIcon />}
                                                onClick={() => handleOpenDetails(req)}
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            )}

            {/* Request Details Dialog */}
            <Dialog open={openModal} onClose={handleCloseDetails} maxWidth="md" fullWidth>
                {selectedRequest && (
                    <>
                        <DialogTitle sx={{ backgroundColor: '#673ab7', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6">Edit History Details</Typography>
                            <IconButton onClick={handleCloseDetails} sx={{ color: 'white' }}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent sx={{ p: 3 }}>
                            <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">Requestor</Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{selectedRequest.requestor?.name || 'N/A'}</Typography>
                                </Grid>
                                {selectedRequest.module === 'course' ? (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">Course Name</Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                {selectedRequest.course?.course_name || selectedRequest.old_data?.course_name || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">Course ID</Typography>
                                            <Typography variant="subtitle1">
                                                {selectedRequest.reference_id ?? 'N/A'}
                                            </Typography>
                                        </Grid>
                                    </>
                                ) : selectedRequest.module !== 'batch_assessments' && selectedRequest.module !== 'batch_profile' ? (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="textSecondary">Student Name (User ID)</Typography>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {selectedRequest.module === 'certificate'
                                                ? `${selectedRequest.certificate?.student_name || 'N/A'} (${selectedRequest.certificate?.user_id || 'N/A'})`
                                                : `${selectedRequest.student?.name || 'N/A'} (${selectedRequest.student?.userid || 'N/A'})`}
                                        </Typography>
                                    </Grid>
                                ) : (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="textSecondary">Batch Name (ID)</Typography>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {selectedRequest.batch?.batch_name || 'N/A'} ({selectedRequest.batch?.id || 'N/A'})
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">Status & Actioned By</Typography>
                                    <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {getStatusChip(selectedRequest.status)}
                                        {selectedRequest.approver && ` by ${selectedRequest.approver.name}`}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">Location & Date</Typography>
                                    <Typography variant="subtitle1">
                                        {selectedRequest.location?.name || 'All'} | {new Date(selectedRequest.created_at).toLocaleDateString()}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" sx={{ mb: 2, color: '#333' }}>Proposed Changes</Typography>
                            {selectedRequest.module === 'marks' && renderMarksComparison(selectedRequest)}
                            {selectedRequest.module === 'student_profile' && renderStudentProfileComparison(selectedRequest)}
                            {selectedRequest.module === 'batch_profile' && renderBatchProfileComparison(selectedRequest)}
                            {selectedRequest.module === 'certificate' && renderCertificateComparison(selectedRequest)}
                            {selectedRequest.module === 'course' && renderCourseComparison(selectedRequest)}
                            {selectedRequest.remarks && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="h6" sx={{ mb: 1, color: '#333' }}>Remarks</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedRequest.remarks}</Typography>
                                </>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ p: 2 }}>
                            <Button variant="outlined" onClick={handleCloseDetails} sx={{ ml: 'auto' }}>
                                Close
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
}
